import { useSelector } from "react-redux";
import { FieldArray, FormikProvider, useFormik } from "formik";
import React, { useCallback, useMemo, useState } from "react";

// Material Imports
import {
  Chip,
  Grid,
  Button,
  Switch,
  Divider,
  Checkbox,
  TextField,
  IconButton,
  FormControl,
  Autocomplete,
  InputAdornment,
  FormControlLabel,
  createFilterOptions,
} from "@mui/material";
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";

// Import CSS
import "./index.css";

import LoadingSpinner from "components/LoadingSpinner";
import ResetPasswordModal from "../ResetPasswordModal";
import useCheckSaasAccountUser from "hooks/useCheckSaasAccountUser";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import PhysiciansInput from "components/pages/manualUpload/forms/PhysiciansInput";
import { useGetHospitalsQuery } from "app/store/transmission/api/transmissionTableApi";

import { isValidArray } from "utils/array";
import {
  checkSaasAccount,
  checkRPMSaaSAccount,
  filterNonUCHAccounts,
} from "utils/account.util";
import { userValidationSchema } from "schemas/userValidationSchema";

import {
  ROLES,
  USER_ROLES,
  EVOKE_ROLES,
  UCH_CLINICS,
  CLINIC_NAMES,
  CLINIC_ROLES,
  USER_ROLE_MAPPER,
  HOSPITAL_RESTRICTED_ROLES,
} from "static/constants";
import { allManufacturers, manufacturers } from "static/manufacturers";
import useCheckRPMSaaSAccountUser from "hooks/useCheckRPMSaaSAccountUser";

const UserModal = (props) => {
  const {
    userModalOpen,
    modalType,
    closeUserModal,
    saveUser,
    selectedUser,
    title,
    processing,
  } = props;

  const filter = createFilterOptions();

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  // Bumped on a refused role change: without a remount the field goes on showing a
  // role that was never applied.
  const [roleFieldKey, setRoleFieldKey] = useState(0);

  // Shown under the Role field. Out of Formik, whose validation would clear it at once.
  const [roleError, setRoleError] = useState("");

  const isSaasAccountUser = useCheckSaasAccountUser();
  const isRPMSaaSUser = useCheckRPMSaaSAccountUser();
  const manufacturerList = isSaasAccountUser ? manufacturers : allManufacturers;

  const { currentUser } = useSelector((state) => state.auth);
  const { accounts, allAccounts } = useSelector((state) => state.account);
  const isAdminUser = currentUser?.role === USER_ROLES.ADMIN;

  const { data: hospitals } = useGetHospitalsQuery();

  const showAccountError = (accounts, role) => {
    const filteredAccounts = filterNonUCHAccounts(accounts);

    return (
      filteredAccounts?.length > 1 &&
      [
        USER_ROLES.CLINIC,
        USER_ROLES.PHYSICIAN,
        USER_ROLES.CLINIC_REVIEWER,
        USER_ROLES.RPM_USER,
      ].includes(role)
    );
  };

  const accountErrorMessage = (accounts, role) => {
    if (!showAccountError(accounts, role)) return;

    return `${USER_ROLE_MAPPER[role]} role can only be associated with one account.`;
  };

  const normalizeAccountValue = (value) => {
    const hasUCHClinic = value?.some((account) =>
      UCH_CLINICS.includes(account)
    );
    const hasUCHAccount = value?.includes(CLINIC_NAMES.UCH);

    if (hasUCHClinic && !hasUCHAccount) {
      return [...value, CLINIC_NAMES.UCH];
    }
    if (!hasUCHClinic && hasUCHAccount) {
      return filterNonUCHAccounts(value);
    }
    return value;
  };

  const accountList = useMemo(() => {
    const accountsArray =
      (isAdminUser ? allAccounts : accounts)?.map((item) => item?.name) || [];

    const accountsList = accountsArray?.includes(CLINIC_NAMES.CENTRA)
      ? [...accountsArray, CLINIC_NAMES.CENTRA_HF]
      : accountsArray;

    return [...new Set(accountsList)];
  }, [accounts, allAccounts, isAdminUser]);

  const getPrimaryRolesList = useCallback(
    (selectedAccounts) => {
      if (isRPMSaaSUser && selectedAccounts?.length === 1) {
        return [USER_ROLES.RPM_USER];
      }

      const isRPMSaaS = checkRPMSaaSAccount(allAccounts, selectedAccounts?.[0]);

      const rolesList = isAdminUser
        ? isRPMSaaS && selectedAccounts?.length === 1
          ? [USER_ROLES.RPM_USER]
          : ROLES.filter((role) => role !== USER_ROLES.RPM_USER)
        : CLINIC_ROLES;

      return [...new Set(rolesList)];
    },
    [allAccounts, isAdminUser, isRPMSaaSUser]
  );

  const getSecondaryRolesList = useCallback(
    (account) => {
      const isRPMOnlyAccount = checkRPMSaaSAccount(allAccounts, account);

      if (isRPMOnlyAccount) {
        const isCliniiUser = selectedUser?.isCliniiUser || false;

        return isCliniiUser ? [USER_ROLES.RPM_USER] : [];
      }

      const isSaasAccount = checkSaasAccount(allAccounts, account);

      const rolesList = isSaasAccount
        ? CLINIC_ROLES
        : CLINIC_ROLES.filter((role) => role !== USER_ROLES.CLINIC_ADMIN);

      return [...new Set(rolesList)];
    },
    [allAccounts]
  );

  const onConfirm = (values) => {
    saveUser(values);
  };

  const onClose = () => {
    closeUserModal();
    setConfirmPasswordVisible(false);
  };

  const toggePasswordVisible = () => {
    setPasswordVisible(!passwordVisible);
  };

  const toggeConfirmPasswordVisible = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  const openResetPasswordModal = () => {
    setResetPasswordModalOpen(true);
  };

  const closeResetPasswordModal = () => {
    setResetPasswordModalOpen(false);
  };

  const formik = useFormik({
    initialValues: { ...selectedUser },
    validationSchema: userValidationSchema(modalType === "edit"),
    onSubmit: onConfirm,
    initialErrors: {},
  });

  const {
    errors,
    values,
    touched,
    resetForm,
    handleSubmit,
    handleChange,
    setFieldValue,
  } = formik;

  // The restriction and a role that cannot hold it must not be saved together. Catches
  // records that already carry the pair.
  const hasHospitalRoleConflict =
    Boolean(values?.isFollowingHospital) &&
    !HOSPITAL_RESTRICTED_ROLES.includes(values?.role);

  return (
    <FormikProvider value={formik}>
      {modalType === "edit" && resetPasswordModalOpen && (
        <ResetPasswordModal
          selectedUser={selectedUser}
          resetPasswordModalOpen={resetPasswordModalOpen}
          closeResetPasswordModal={closeResetPasswordModal}
          title={`Reset User Password for ${selectedUser?.name}`}
        />
      )}

      <ModalWrapper
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        show={userModalOpen}
        onClose={closeUserModal}
        title={title}
        maxWidth="md"
        actionButtons={[
          ...(modalType === "edit"
            ? [
                {
                  label: "Reset Password",
                  variant: "text",
                  className: "mr-2",
                  isLeft: true,
                  onClick: openResetPasswordModal,
                  disabled: processing,
                },
              ]
            : []),
          {
            label: "Cancel",
            variant: "outlined",
            className: "mr-2",
            onClick: () => {
              resetForm();
              onClose();
            },
            disabled: processing,
          },
          {
            label: "Save",
            onClick: handleSubmit,
            isLoading: processing,
            // Also blocked while a refused change stands, or saving would look accepted.
            disabled:
              processing || hasHospitalRoleConflict || Boolean(roleError),
          },
        ]}
      >
        <>
          {processing ? (
            <div className="user-modal__input d-flex flex-column align-items-center">
              <div className="user-modal__processing">Processing user</div>
              <div className="user-modal__spinner">
                <LoadingSpinner />
              </div>
            </div>
          ) : (
            <form>
              <div className="user-modal__text">
                <div>
                  <FormControl fullWidth margin="normal">
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      id="name"
                      label="Name"
                      value={values.name}
                      onChange={handleChange("name")}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      id="email"
                      label="Email"
                      value={values.email}
                      onChange={handleChange("email")}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      autoComplete="email"
                      disabled={values.idp_user}
                    />
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <Autocomplete
                      multiple
                      disableClearable
                      freeSolo={false}
                      disableCloseOnSelect
                      clearOnBlur
                      selectOnFocus
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);
                        return filtered.length ? filtered : options;
                      }}
                      required
                      fullWidth
                      id="accountArr"
                      name="accountArr"
                      openOnFocus={true}
                      value={values.accountArr || []}
                      renderTags={(value, getTagProps) => {
                        const visible = filterNonUCHAccounts(value);
                        return visible?.map((account) => {
                          const originalIndex = value.indexOf(account);
                          return (
                            <Chip
                              {...getTagProps({ index: originalIndex })}
                              key={account}
                              label={account}
                              size="small"
                              variant="outlined"
                              deleteIcon={<ClearIcon />}
                              onDelete={() => {
                                const selectedAccounts = normalizeAccountValue(
                                  value.filter((v) => v !== account)
                                );
                                setFieldValue("accountArr", selectedAccounts);

                                if (
                                  values.role === USER_ROLES.RPM_USER &&
                                  selectedAccounts?.length > 1
                                ) {
                                  setFieldValue("role", "");
                                }
                              }}
                            />
                          );
                        });
                      }}
                      options={accountList || []}
                      onChange={(e, value) => {
                        const selectedAccounts = normalizeAccountValue(value);

                        setFieldValue("accountArr", selectedAccounts);

                        const isRPMSaaSUser =
                          values.role === USER_ROLES.RPM_USER;

                        if (isRPMSaaSUser && selectedAccounts?.length > 1) {
                          setFieldValue("role", "");
                        }
                      }}
                      renderOption={(props, option, { selected }) => {
                        const { key, ...optionProps } = props;
                        if (option === CLINIC_NAMES.UCH) return;

                        return (
                          <li key={key} {...optionProps}>
                            <Checkbox checked={selected} />
                            {option}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Accounts"
                          variant="outlined"
                          type="text"
                          required
                          placeholder={
                            values.accountArr?.length ? "" : "Search accounts"
                          }
                          error={
                            touched.accountArr &&
                            (Boolean(errors.accountArr) ||
                              showAccountError(values.accountArr, values.role))
                          }
                          helperText={
                            touched.accountArr &&
                            (errors.accountArr ||
                              accountErrorMessage(
                                values.accountArr,
                                values.role
                              ))
                          }
                        />
                      )}
                    />
                  </FormControl>
                  <FormControl fullWidth margin="normal">
                    <Autocomplete
                      multiple
                      disableClearable
                      freeSolo={false}
                      disableCloseOnSelect
                      clearOnBlur
                      selectOnFocus
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);
                        return filtered.length ? filtered : options;
                      }}
                      required
                      fullWidth
                      id="manufacturerArr"
                      name="manufacturerArr"
                      openOnFocus={true}
                      value={values.manufacturerArr || []}
                      renderTags={(value, getTagProps) =>
                        value?.map((manufacturer, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={manufacturer}
                            label={manufacturer}
                            size="small"
                            variant="outlined"
                            deleteIcon={<ClearIcon />}
                            onDelete={() => {
                              setFieldValue(
                                "manufacturerArr",
                                value.filter((v) => v !== manufacturer)
                              );
                            }}
                          />
                        ))
                      }
                      options={manufacturerList || []}
                      onChange={(e, value) =>
                        setFieldValue("manufacturerArr", value)
                      }
                      renderOption={(props, option, { selected }) => {
                        const { key, ...optionProps } = props;
                        return (
                          <li key={key} {...optionProps}>
                            <Checkbox checked={selected} />
                            {option}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Manufacturers"
                          name="manufacturerArr"
                          variant="outlined"
                          type="text"
                          required
                          placeholder={
                            values.manufacturerArr?.length
                              ? ""
                              : "Search manufacturers"
                          }
                          error={
                            touched.manufacturerArr &&
                            Boolean(errors.manufacturerArr)
                          }
                          helperText={
                            touched.manufacturerArr && errors.manufacturerArr
                          }
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <Autocomplete
                      key={roleFieldKey}
                      id="role"
                      name="role"
                      // The message refers to the last attempt, so reopening clears it —
                      // otherwise there is no way to dismiss it and Save stays disabled.
                      onOpen={() => setRoleError("")}
                      required
                      disableClearable
                      freeSolo={false}
                      value={values.role}
                      options={getPrimaryRolesList(values?.accountArr)}
                      getOptionLabel={(option) =>
                        USER_ROLE_MAPPER[option] || ""
                      }
                      onChange={(e, value) => {
                        // The restriction's control is hidden for other roles, so allowing
                        // this would hide a setting still saved on the user.
                        if (
                          values?.isFollowingHospital &&
                          !HOSPITAL_RESTRICTED_ROLES.includes(value)
                        ) {
                          setRoleError(
                            "Turn off “Restrict to assigned hospitals” first — this role cannot have hospitals assigned."
                          );
                          setRoleFieldKey((key) => key + 1);
                          return;
                        }

                        setRoleError("");
                        setFieldValue("role", value);

                        if (CLINIC_ROLES.includes(value)) {
                          setFieldValue("secondary_roles", []);
                        }

                        if (
                          value === USER_ROLES.RPM_USER &&
                          values?.accountArr?.length > 0
                        ) {
                          setFieldValue("isCliniiUser", true);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Role"
                          name="role"
                          variant="outlined"
                          type="text"
                          required
                          inputProps={{
                            ...params.inputProps,
                            readOnly: true,
                          }}
                          error={
                            Boolean(roleError) ||
                            hasHospitalRoleConflict ||
                            (touched.role && Boolean(errors.role))
                          }
                          helperText={
                            roleError ||
                            (hasHospitalRoleConflict
                              ? "Turn off “Restrict to assigned hospitals” below — this role cannot have hospitals assigned."
                              : touched.role && errors.role)
                          }
                        />
                      )}
                    />
                  </FormControl>

                  {values?.accountArr?.length > 0 &&
                    EVOKE_ROLES.includes(values.role) && (
                      <FieldArray
                        name="secondary_roles"
                        render={({ push, remove }) => (
                          <Grid container>
                            <Grid container item>
                              {values?.secondary_roles?.map((data, index) => {
                                return (
                                  <Grid
                                    container
                                    key={index}
                                    columnSpacing={1}
                                    alignItems="flex-start"
                                  >
                                    <Grid item xs={5.5}>
                                      <FormControl fullWidth margin="normal">
                                        <Autocomplete
                                          id="account"
                                          name={`secondary_roles[${index}].account`}
                                          required
                                          disableClearable
                                          freeSolo={false}
                                          value={data?.account}
                                          options={
                                            isValidArray(values.accountArr)
                                              ? filterNonUCHAccounts(
                                                  values.accountArr
                                                )
                                              : []
                                          }
                                          getOptionDisabled={(option) =>
                                            values?.secondary_roles?.some(
                                              (role, i) =>
                                                role.account === option &&
                                                i !== index
                                            )
                                          }
                                          onChange={(_e, value) =>
                                            setFieldValue(
                                              `secondary_roles[${index}].account`,
                                              value
                                            )
                                          }
                                          renderInput={(params) => {
                                            const hasTouched =
                                              touched?.secondary_roles?.[index]
                                                ?.account;
                                            const errorMessage =
                                              errors?.secondary_roles?.[index]
                                                ?.account;

                                            return (
                                              <TextField
                                                {...params}
                                                label="Account"
                                                variant="outlined"
                                                type="text"
                                                required
                                                placeholder="Search account"
                                                error={
                                                  hasTouched &&
                                                  Boolean(errorMessage)
                                                }
                                                helperText={
                                                  hasTouched && errorMessage
                                                }
                                              />
                                            );
                                          }}
                                        />
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormControl fullWidth margin="normal">
                                        <Autocomplete
                                          multiple
                                          disableClearable
                                          freeSolo={false}
                                          disableCloseOnSelect
                                          fullWidth
                                          id="roles"
                                          name={`secondary_roles[${index}].roles`}
                                          value={data?.roles || []}
                                          options={getSecondaryRolesList(
                                            data.account
                                          )}
                                          onChange={(_e, value) =>
                                            setFieldValue(
                                              `secondary_roles[${index}].roles`,
                                              value
                                            )
                                          }
                                          getOptionLabel={(option) =>
                                            USER_ROLE_MAPPER[option] || ""
                                          }
                                          renderOption={(
                                            props,
                                            option,
                                            { selected }
                                          ) => {
                                            const { key, ...optionProps } =
                                              props;
                                            return (
                                              <li
                                                key={`account-${key}`}
                                                {...optionProps}
                                              >
                                                <Checkbox checked={selected} />
                                                {USER_ROLE_MAPPER[option]}
                                              </li>
                                            );
                                          }}
                                          ChipProps={{
                                            variant: "outlined",
                                            deleteIcon: <ClearIcon />,
                                          }}
                                          renderInput={(params) => {
                                            const hasTouched =
                                              touched?.secondary_roles?.[index]
                                                ?.roles;
                                            const errorMessage =
                                              errors?.secondary_roles?.[index]
                                                ?.roles;

                                            return (
                                              <TextField
                                                {...params}
                                                label="Secondary Roles"
                                                variant="outlined"
                                                type="text"
                                                required
                                                maxRows={1}
                                                inputProps={{
                                                  ...params.inputProps,
                                                  readOnly: true,
                                                }}
                                                error={
                                                  hasTouched &&
                                                  Boolean(errorMessage)
                                                }
                                                helperText={
                                                  hasTouched && errorMessage
                                                }
                                              />
                                            );
                                          }}
                                        />
                                      </FormControl>
                                    </Grid>

                                    {values?.secondary_roles?.length > 0 && (
                                      <Grid
                                        item
                                        xs={0.5}
                                        style={{ marginTop: 32 }}
                                      >
                                        <DeleteOutlineIcon
                                          className="rpm-device-information__delete ml-2 mt-0"
                                          onClick={() => remove(index)}
                                        />
                                      </Grid>
                                    )}
                                  </Grid>
                                );
                              })}
                            </Grid>

                            {values?.accountArr?.length > 0 &&
                              values?.accountArr?.length >
                                values?.secondary_roles?.length && (
                                <Grid container>
                                  <Button
                                    variant="text"
                                    color="secondary"
                                    startIcon={<AddIcon />}
                                    onClick={() =>
                                      push({
                                        account: "",
                                        roles: [],
                                      })
                                    }
                                  >
                                    Add Secondary Role
                                  </Button>
                                </Grid>
                              )}
                          </Grid>
                        )}
                      />
                    )}

                  {modalType === "add" && (
                    <>
                      <FormControl fullWidth margin="normal">
                        <TextField
                          fullWidth
                          required
                          variant="outlined"
                          id="password"
                          label="Password"
                          value={values.password}
                          type={passwordVisible ? "text" : "password"}
                          placeholder="Password must be at least 12 characters long."
                          minLength={12}
                          onChange={handleChange("password")}
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={toggePasswordVisible}
                                  onMouseDown={toggePasswordVisible}
                                >
                                  {passwordVisible ? (
                                    <VisibilityIcon />
                                  ) : (
                                    <VisibilityOffIcon />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          autoComplete="new-password"
                        />
                      </FormControl>

                      <FormControl fullWidth margin="normal">
                        <TextField
                          fullWidth
                          required
                          variant="outlined"
                          id="passwordConfirm"
                          label="Confirm Password"
                          value={values.passwordConfirm}
                          type={confirmPasswordVisible ? "text" : "password"}
                          placeholder="Password must be at least 12 characters long."
                          minLength={12}
                          onChange={handleChange("passwordConfirm")}
                          error={
                            touched.passwordConfirm &&
                            Boolean(errors.passwordConfirm)
                          }
                          helperText={
                            touched.passwordConfirm && errors.passwordConfirm
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={toggeConfirmPasswordVisible}
                                  onMouseDown={toggeConfirmPasswordVisible}
                                >
                                  {confirmPasswordVisible ? (
                                    <VisibilityIcon />
                                  ) : (
                                    <VisibilityOffIcon />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          autoComplete="new-password"
                        />
                      </FormControl>
                    </>
                  )}
                  <Divider
                    textAlign="left"
                    sx={{
                      mt: 3,
                      mb: 2,
                      "&::before": { display: "none" },
                      "&::after": { borderColor: "rgba(0,0,0,0.12)" },
                      "& .MuiDivider-wrapper": { pl: 0 },
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        color: "#444",
                        fontWeight: 500,
                      }}
                    >
                      Additional Settings
                    </span>
                  </Divider>

                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              values.role === USER_ROLES.RPM_USER ||
                              values?.isCliniiUser ||
                              false
                            }
                            disabled={values.role === USER_ROLES.RPM_USER}
                            onChange={(e, c) => {
                              setFieldValue("isCliniiUser", c);
                            }}
                            color="secondary"
                          />
                        }
                        label="Clinii User"
                        sx={{
                          ml: 0,
                          "& .MuiFormControlLabel-label": {
                            color:
                              values.role === USER_ROLES.RPM_USER ||
                              values?.isCliniiUser
                                ? "success.main"
                                : undefined,
                          },
                        }}
                      />
                    </Grid>

                    {CLINIC_ROLES.includes(values?.role) && (
                      <>
                        {[USER_ROLES.PHYSICIAN].includes(values?.role) && (
                          <Grid item xs={12} sm={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={values?.testUser || false}
                                  onChange={(e, c) => {
                                    setFieldValue("testUser", c);
                                  }}
                                  color="secondary"
                                />
                              }
                              label="Test User"
                              sx={{
                                ml: 0,
                                "& .MuiFormControlLabel-label": {
                                  color: values?.testUser
                                    ? "success.main"
                                    : undefined,
                                },
                              }}
                            />
                          </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values?.showHFReports || false}
                                onChange={(e, c) => {
                                  setFieldValue("showHFReports", c);
                                }}
                                color="secondary"
                              />
                            }
                            label="Show HF Reports Only"
                            sx={{
                              ml: 0,
                              "& .MuiFormControlLabel-label": {
                                color: values?.showHFReports
                                  ? "success.main"
                                  : undefined,
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values?.showRegularReports || false}
                                onChange={(e, c) => {
                                  setFieldValue("showRegularReports", c);
                                }}
                                color="secondary"
                              />
                            }
                            label="Show Regular Reports Only"
                            sx={{
                              ml: 0,
                              "& .MuiFormControlLabel-label": {
                                color: values?.showRegularReports
                                  ? "success.main"
                                  : undefined,
                              },
                            }}
                          />
                        </Grid>

                        {[USER_ROLES.PHYSICIAN].includes(values?.role) && (
                          <Grid item xs={12} sm={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={values?.myPatientsToggled || false}
                                  onChange={(e, c) => {
                                    setFieldValue("myPatientsToggled", c);
                                  }}
                                  color="secondary"
                                />
                              }
                              label="Default My Patients Toggle"
                              sx={{
                                ml: 0,
                                "& .MuiFormControlLabel-label": {
                                  color: values?.myPatientsToggled
                                    ? "success.main"
                                    : undefined,
                                },
                              }}
                            />
                          </Grid>
                        )}

                        {[USER_ROLES.CLINIC].includes(values?.role) &&
                          values?.accountArr?.some((account) =>
                            UCH_CLINICS.includes(account)
                          ) && (
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={
                                      values?.isFollowingPhysician || false
                                    }
                                    onChange={(e, c) => {
                                      setFieldValue("isFollowingPhysician", c);
                                    }}
                                    color="secondary"
                                  />
                                }
                                label="Follow Physician"
                                sx={{
                                  ml: 0,
                                  "& .MuiFormControlLabel-label": {
                                    color: values?.isFollowingPhysician
                                      ? "success.main"
                                      : undefined,
                                  },
                                }}
                              />

                              {values?.isFollowingPhysician && (
                                <PhysiciansInput
                                  label="Following Physicians"
                                  fields={{
                                    ...values,
                                    account: values?.accountArr[0],
                                  }}
                                  onUpdate={(e, c) => {
                                    setFieldValue("physicians", c);
                                  }}
                                />
                              )}
                            </Grid>
                          )}

                        {(HOSPITAL_RESTRICTED_ROLES.includes(values?.role) ||
                          hasHospitalRoleConflict) && (
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={values?.isFollowingHospital || false}
                                  onChange={(e, c) => {
                                    setFieldValue("isFollowingHospital", c);
                                    if (!c) {
                                      setRoleError("");
                                    }
                                  }}
                                  color="secondary"
                                />
                              }
                              label="Restrict to assigned hospitals"
                              sx={{
                                ml: 0,
                                "& .MuiFormControlLabel-label": {
                                  color: values?.isFollowingHospital
                                    ? "success.main"
                                    : undefined,
                                },
                              }}
                            />

                            {values?.isFollowingHospital && (
                              <FormControl fullWidth sx={{ mt: 1 }}>
                                <Autocomplete
                                  multiple
                                  disableClearable
                                  freeSolo={false}
                                  disableCloseOnSelect
                                  clearOnBlur
                                  selectOnFocus
                                  filterOptions={(options, params) => {
                                    const filtered = filter(options, params);
                                    return filtered.length ? filtered : options;
                                  }}
                                  fullWidth
                                  id="hospitals"
                                  name="hospitals"
                                  openOnFocus={true}
                                  value={values?.hospitals || []}
                                  renderTags={(value, getTagProps) =>
                                    value?.map((hospital, index) => (
                                      <Chip
                                        {...getTagProps({ index })}
                                        key={hospital}
                                        label={hospital}
                                        size="small"
                                        variant="outlined"
                                        deleteIcon={<ClearIcon />}
                                        onDelete={() => {
                                          setFieldValue(
                                            "hospitals",
                                            value.filter((v) => v !== hospital)
                                          );
                                        }}
                                      />
                                    ))
                                  }
                                  options={[
                                    ...new Set(
                                      (hospitals || [])
                                        .map(({ name }) =>
                                          String(name ?? "").trim()
                                        )
                                        .filter(Boolean)
                                    ),
                                  ]}
                                  getOptionLabel={(option) => option || ""}
                                  onChange={(e, value) =>
                                    setFieldValue("hospitals", value)
                                  }
                                  renderOption={(
                                    props,
                                    option,
                                    { selected }
                                  ) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                      <li key={key} {...optionProps}>
                                        <Checkbox checked={selected} />
                                        {option}
                                      </li>
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Assigned Hospitals"
                                      name="hospitals"
                                      variant="outlined"
                                      type="text"
                                      placeholder={
                                        values?.hospitals?.length
                                          ? ""
                                          : "Search hospitals"
                                      }
                                    />
                                  )}
                                />
                              </FormControl>
                            )}
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </div>
              </div>
            </form>
          )}
        </>
      </ModalWrapper>
    </FormikProvider>
  );
};

export default UserModal;
